import {
  useQueryAssetShares,
  AssetShare,
  ListAssetSharesInput,
} from "./useQueryAssetShares";
import { useAccount } from "../account/useAccount";
import { useTeams } from "../member/useTeams";
import { useMembers } from "../member/useMembers";
import { useMemo } from "react";
import { type DiscriminatedMerge } from "@zapier/utils";

interface HydratedSubject {
  account: { account?: ReturnType<typeof useAccount>["account"] };
  accountadmin: { account?: ReturnType<typeof useAccount>["account"] };
  team: { team?: ReturnType<typeof useTeams>["teams"][string] };
  user: { user?: ReturnType<typeof useMembers>["members"][string] };
  url_secret: Record<never, never>;
}

export type HydratedAssetShare = DiscriminatedMerge<
  AssetShare,
  "subjectType",
  HydratedSubject
>;

interface Options {
  enabled?: boolean;
}

/** A simple replacement for Object.groupBy that works without needing to bump the `lib` */
function groupBy<K extends PropertyKey, T>(
  array: T[],
  key: (item: T) => K,
): Record<K, T[]> {
  const result: Record<K, T[]> = {} as Record<K, T[]>;

  for (const item of array) {
    const groupKey = key(item);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
  }

  return result;
}

function dedupeSharesBySubject(shares: AssetShare[]): AssetShare[] {
  const seen = new Set<string>();
  return shares.filter((share) => {
    const key = `${share.subjectType}:${share.subjectId}:${share.group ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function useFetchAssetShares(
  input: ListAssetSharesInput,
  options?: Options,
) {
  // Only stop if explicitly disabled
  const isEnabled = options?.enabled !== false;

  const {
    shares: rawShares,
    isLoading,
    isFetching,
  } = useQueryAssetShares(input, {
    enabled: isEnabled,
  });

  const shares = useMemo(() => dedupeSharesBySubject(rawShares), [rawShares]);

  const idsByType = useMemo<Record<AssetShare["subjectType"], number[]>>(() => {
    const groupedIds: Record<string, number[]> = {};
    for (const share of shares) {
      if (!groupedIds[share.subjectType]) {
        groupedIds[share.subjectType] = [];
      }
      groupedIds[share.subjectType].push(Number(share.subjectId));
    }
    return groupedIds;
  }, [shares]);

  // Hydrate account
  const accountShare = idsByType["account"]?.[0];
  const { account, isLoading: isLoadingAccount } = useAccount({
    enabled: Boolean(accountShare),
  });

  // Hydrate teams
  const teamIds = idsByType["team"] ?? [];
  const { teams, isLoading: isLoadingTeams } = useTeams({ ids: teamIds });

  // Hydrate users
  const userIds = idsByType["user"] ?? [];
  const { members, isLoading: isLoadingUsers } = useMembers({ ids: userIds });

  // Compose hydrated shares
  const hydratedShares: HydratedAssetShare[] = useMemo(() => {
    return shares.map((share) => {
      // Destructure here so TypeScript can know we're returning the narrowed `subjectType`
      const { subjectType, ...base } = share;

      switch (subjectType) {
        case "account": {
          return { ...base, subjectType, account };
        }
        case "team": {
          return { ...base, subjectType, team: teams[share.subjectId] };
        }
        case "url_secret": {
          return { ...base, subjectType };
        }
        case "user": {
          return { ...base, subjectType, user: members[share.subjectId] };
        }
        case "accountadmin": {
          return { ...base, subjectType, account };
        }
      }
    });
  }, [shares, account, teams, members]);

  const sharesByType = useMemo(() => {
    return groupBy(hydratedShares, (share) => share.subjectType);
  }, [hydratedShares]);

  return {
    shares: hydratedShares,
    sharesByType,
    isLoading:
      isLoading || isLoadingAccount || isLoadingTeams || isLoadingUsers,
    isFetching,
  };
}
